import React from 'react';
import { Typography, Box, Stack, Chip, Grid, Button } from '@mui/material';
import { TrendingUp, ShoppingCart, People, LocalOffer } from '@mui/icons-material';
import LiveEditor from '../components/Playground/LiveEditor';
import { AutobiaGrid, AutobiaContainer, AutobiaStack, AutobiaFlex } from '../components/Layout/AutobiaGrid';
import AutobiaCard, { AutobiaStatsCard, AutobiaImageCard } from '../components/Layout/AutobiaCard';

const Layout = () => {
  // Component Examples
  const GridExample = () => (
    <AutobiaContainer maxWidth="lg">
      <AutobiaGrid container spacing={3}>
        <AutobiaGrid item xs={12}>
          <Box sx={{ p: 2, backgroundColor: 'primary.main', color: 'white', textAlign: 'center', borderRadius: 1, fontWeight: 600 }}>
            Full Width (xs=12)
          </Box>
        </AutobiaGrid>
        <AutobiaGrid item xs={12} sm={6}>
          <Box sx={{ p: 2, backgroundColor: 'secondary.main', color: 'white', textAlign: 'center', borderRadius: 1, fontWeight: 600 }}>
            Half on Small+ (xs=12, sm=6)
          </Box>
        </AutobiaGrid>
        <AutobiaGrid item xs={12} sm={6}>
          <Box sx={{ p: 2, backgroundColor: 'secondary.main', color: 'white', textAlign: 'center', borderRadius: 1, fontWeight: 600 }}>
            Half on Small+ (xs=12, sm=6)
          </Box>
        </AutobiaGrid>
        <AutobiaGrid item xs={12} sm={6} md={4}>
          <Box sx={{ p: 2, backgroundColor: 'success.main', color: 'white', textAlign: 'center', borderRadius: 1, fontWeight: 600 }}>
            Third on Medium+ (xs=12, sm=6, md=4)
          </Box>
        </AutobiaGrid>
        <AutobiaGrid item xs={12} sm={6} md={4}>
          <Box sx={{ p: 2, backgroundColor: 'success.main', color: 'white', textAlign: 'center', borderRadius: 1, fontWeight: 600 }}>
            Third on Medium+ (xs=12, sm=6, md=4)
          </Box>
        </AutobiaGrid>
        <AutobiaGrid item xs={12} sm={6} md={4}>
          <Box sx={{ p: 2, backgroundColor: 'success.main', color: 'white', textAlign: 'center', borderRadius: 1, fontWeight: 600 }}>
            Third on Medium+ (xs=12, sm=6, md=4)
          </Box>
        </AutobiaGrid>
      </AutobiaGrid>
    </AutobiaContainer>
  );

  const StackExample = () => (
    <Stack spacing={3}>
      {/* Vertical Stack */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>Vertical Stack (Default)</Typography>
        <AutobiaStack spacing={2}>
          <Box sx={{ p: 2, backgroundColor: 'primary.main', color: 'white', textAlign: 'center', borderRadius: 1, fontWeight: 600 }}>
            Item 1
          </Box>
          <Box sx={{ p: 2, backgroundColor: 'primary.main', color: 'white', textAlign: 'center', borderRadius: 1, fontWeight: 600 }}>
            Item 2
          </Box>
          <Box sx={{ p: 2, backgroundColor: 'primary.main', color: 'white', textAlign: 'center', borderRadius: 1, fontWeight: 600 }}>
            Item 3
          </Box>
        </AutobiaStack>
      </Box>

      {/* Horizontal Stack */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>Horizontal Stack</Typography>
        <AutobiaStack direction="row" spacing={2}>
          <Box sx={{ p: 2, backgroundColor: 'secondary.main', color: 'white', textAlign: 'center', borderRadius: 1, minWidth: 100, fontWeight: 600 }}>
            Item 1
          </Box>
          <Box sx={{ p: 2, backgroundColor: 'secondary.main', color: 'white', textAlign: 'center', borderRadius: 1, minWidth: 100, fontWeight: 600 }}>
            Item 2
          </Box>
          <Box sx={{ p: 2, backgroundColor: 'secondary.main', color: 'white', textAlign: 'center', borderRadius: 1, minWidth: 100, fontWeight: 600 }}>
            Item 3
          </Box>
        </AutobiaStack>
      </Box>

      {/* Responsive Stack */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>Responsive Stack (Column on Mobile, Row on Desktop)</Typography>
        <AutobiaStack direction="row" responsive spacing={2}>
          <Box sx={{ p: 2, backgroundColor: 'success.main', color: 'white', textAlign: 'center', borderRadius: 1, minWidth: 120, fontWeight: 600 }}>
            Responsive 1
          </Box>
          <Box sx={{ p: 2, backgroundColor: 'success.main', color: 'white', textAlign: 'center', borderRadius: 1, minWidth: 120, fontWeight: 600 }}>
            Responsive 2
          </Box>
          <Box sx={{ p: 2, backgroundColor: 'success.main', color: 'white', textAlign: 'center', borderRadius: 1, minWidth: 120, fontWeight: 600 }}>
            Responsive 3
          </Box>
        </AutobiaStack>
      </Box>
    </Stack>
  );

  const FlexExample = () => (
    <Stack spacing={3}>
      {/* Center Alignment */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>Centered Flex</Typography>
        <AutobiaFlex
          justifyContent="center"
          alignItems="center"
          gap={2}
          sx={{ p: 3, backgroundColor: 'gray.g100', borderRadius: 2, minHeight: 120, border: '2px dashed', borderColor: 'gray.g300' }}
        >
          <Button variant="contained">Center</Button>
          <Button variant="outlined">Items</Button>
        </AutobiaFlex>
      </Box>

      {/* Space Between */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>Space Between</Typography>
        <AutobiaFlex
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 3, backgroundColor: 'gray.g100', borderRadius: 2, border: '2px dashed', borderColor: 'gray.g300' }}
        >
          <Typography variant="h6">Title</Typography>
          <Button variant="contained">Action</Button>
        </AutobiaFlex>
      </Box>

      {/* Flex Wrap */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>Flex Wrap</Typography>
        <AutobiaFlex
          wrap="wrap"
          gap={1}
          sx={{ p: 3, backgroundColor: 'gray.g100', borderRadius: 2, border: '2px dashed', borderColor: 'gray.g300' }}
        >
          {['Tag 1', 'Tag 2', 'Tag 3', 'Tag 4', 'Tag 5', 'Tag 6', 'Tag 7', 'Tag 8'].map((tag) => (
            <Chip key={tag} label={tag} />
          ))}
        </AutobiaFlex>
      </Box>
    </Stack>
  );

  const CardExample = () => (
    <Grid container spacing={3}>
      {/* Basic Card */}
      <Grid item xs={12} md={6}>
        <AutobiaCard
          title="Basic Card"
          subtitle="This is a subtitle"
        >
          <Typography variant="body2" color="gray.g600">
            This is the main content area of the card. You can put any content here
            including text, images, forms, or other components.
          </Typography>
        </AutobiaCard>
      </Grid>

      {/* Card with Actions */}
      <Grid item xs={12} md={6}>
        <AutobiaCard
          title="Card with Actions"
          subtitle="Interactive card example"
          actions={
            <Stack direction="row" spacing={1}>
              <Button size="small">Cancel</Button>
              <Button size="small" variant="contained">Save</Button>
            </Stack>
          }
        >
          <Typography variant="body2" color="gray.g600">
            This card includes action buttons at the bottom.
          </Typography>
        </AutobiaCard>
      </Grid>

      {/* Stats Cards */}
      <Grid item xs={12} sm={6} md={3}>
        <AutobiaStatsCard
          title="Total Revenue"
          value="245,680 SAR"
          icon={<TrendingUp />}
          trend={12.5}
          trendLabel="vs last month"
          color="primary"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <AutobiaStatsCard
          title="Orders"
          value="1,247"
          icon={<ShoppingCart />}
          trend={-2.3}
          trendLabel="vs last month"
          color="secondary"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <AutobiaStatsCard
          title="Customers"
          value="856"
          icon={<People />}
          trend={8.1}
          trendLabel="vs last month"
          color="success"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <AutobiaStatsCard
          title="Conversion"
          value="3.2%"
          icon={<LocalOffer />}
          trend={0.8}
          trendLabel="vs last month"
          color="warning"
        />
      </Grid>

      {/* Image Card */}
      <Grid item xs={12} md={6}>
        <AutobiaImageCard
          image="data:image/svg+xml,%3Csvg width='400' height='200' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='100%25' height='100%25' fill='%23FA9940'/%3E%3Ctext x='50%25' y='50%25' font-size='20' text-anchor='middle' dy='.3em' fill='white'%3EProduct Image%3C/svg%3E"
          title="Product Card"
          description="This is an example of a card with an image header. Perfect for product displays or content previews."
          actions={
            <Stack direction="row" spacing={1}>
              <Button size="small">View</Button>
              <Button size="small" variant="contained">Buy Now</Button>
            </Stack>
          }
        />
      </Grid>
    </Grid>
  );

  return (
    <Box>
      <Stack spacing={2} sx={{ mb: 6 }}>
        <Typography variant="h2" fontWeight={700} color="gray.g700">
          Layout Components
        </Typography>
        <Typography variant="body1" color="gray.g600" sx={{ maxWidth: 600 }}>
          Layout components for organizing content, creating responsive grids, 
          and building structured page layouts with Autobia design standards.
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          <Chip label="Grid System" color="primary" variant="outlined" />
          <Chip label="Responsive Design" variant="outlined" />
          <Chip label="Content Containers" variant="outlined" />
        </Stack>
      </Stack>

      <Stack spacing={6}>
        {/* Grid System */}
        <LiveEditor
          title="Grid System"
          description="Responsive grid system for creating flexible layouts across all screen sizes."
          component={GridExample}
          code={`import { AutobiaGrid, AutobiaContainer } from './components/Layout/AutobiaGrid';

const Example = () => (
  <AutobiaContainer maxWidth="lg">
    <AutobiaGrid container spacing={3}>
      <AutobiaGrid item xs={12}>
        <Box sx={{ p: 2, backgroundColor: 'primary.main', color: 'white', fontWeight: 600 }}>
          Full Width (xs=12)
        </Box>
      </AutobiaGrid>
      
      <AutobiaGrid item xs={12} sm={6}>
        <Box sx={{ p: 2, backgroundColor: 'secondary.main', color: 'white', fontWeight: 600 }}>
          Half on Small+ (xs=12, sm=6)
        </Box>
      </AutobiaGrid>
      <AutobiaGrid item xs={12} sm={6}>
        <Box sx={{ p: 2, backgroundColor: 'secondary.main', color: 'white', fontWeight: 600 }}>
          Half on Small+ (xs=12, sm=6)
        </Box>
      </AutobiaGrid>
      
      <AutobiaGrid item xs={12} sm={6} md={4}>
        <Box sx={{ p: 2, backgroundColor: 'success.main', color: 'white', fontWeight: 600 }}>
          Third on Medium+ (xs=12, sm=6, md=4)
        </Box>
      </AutobiaGrid>
      <AutobiaGrid item xs={12} sm={6} md={4}>
        <Box sx={{ p: 2, backgroundColor: 'success.main', color: 'white', fontWeight: 600 }}>
          Third on Medium+ (xs=12, sm=6, md=4)
        </Box>
      </AutobiaGrid>
      <AutobiaGrid item xs={12} sm={6} md={4}>
        <Box sx={{ p: 2, backgroundColor: 'success.main', color: 'white', fontWeight: 600 }}>
          Third on Medium+ (xs=12, sm=6, md=4)
        </Box>
      </AutobiaGrid>
    </AutobiaGrid>
  </AutobiaContainer>
);

// Props:
// container: boolean - Makes this a flex container
// item: boolean - Makes this a flex item
// xs, sm, md, lg, xl: number (1-12) - Grid breakpoint sizes
// spacing: number - Space between items
// direction: 'row' | 'column' - Flex direction
// justifyContent: flex justify-content values
// alignItems: flex align-items values`}
        />

        {/* Stack Layout */}
        <LiveEditor
          title="Stack & Flex Layouts"
          description="Stack and flexible layout components for arranging elements with consistent spacing."
          component={StackExample}
          code={`import { AutobiaStack, AutobiaFlex } from './components/Layout/AutobiaGrid';

const Example = () => (
  <Stack spacing={3}>
    {/* Vertical Stack */}
    <AutobiaStack spacing={2}>
      <Box>Item 1</Box>
      <Box>Item 2</Box>
      <Box>Item 3</Box>
    </AutobiaStack>

    {/* Horizontal Stack */}
    <AutobiaStack direction="row" spacing={2}>
      <Box>Item 1</Box>
      <Box>Item 2</Box>
      <Box>Item 3</Box>
    </AutobiaStack>

    {/* Responsive Stack */}
    <AutobiaStack direction="row" responsive spacing={2}>
      <Box>Responsive 1</Box>
      <Box>Responsive 2</Box>
      <Box>Responsive 3</Box>
    </AutobiaStack>
  </Stack>
);

// AutobiaStack Props:
// direction: 'row' | 'column' - Stack direction
// spacing: number - Space between items
// responsive: boolean - Column on mobile, row on desktop
// alignItems: flex align-items values
// justifyContent: flex justify-content values
// divider: ReactNode - Element to show between items

// AutobiaFlex Props:
// direction: 'row' | 'column' - Flex direction
// wrap: 'nowrap' | 'wrap' | 'wrap-reverse' - Flex wrap
// justifyContent: flex justify-content values
// alignItems: flex align-items values
// gap: number - Gap between items`}
        />

        {/* Flex Utilities */}
        <LiveEditor
          title="Flex Utilities"
          description="Flexible layout utilities for precise control over element positioning and spacing."
          component={FlexExample}
          code={`import { AutobiaFlex } from './components/Layout/AutobiaGrid';

const Example = () => (
  <Stack spacing={3}>
    {/* Center Alignment */}
    <AutobiaFlex
      justifyContent="center"
      alignItems="center"
      gap={2}
      sx={{ p: 3, backgroundColor: 'gray.g50', minHeight: 120 }}
    >
      <Button>Center</Button>
      <Button>Items</Button>
    </AutobiaFlex>

    {/* Space Between */}
    <AutobiaFlex
      justifyContent="space-between"
      alignItems="center"
      sx={{ p: 3, backgroundColor: 'gray.g50' }}
    >
      <Typography variant="h6">Title</Typography>
      <Button variant="contained">Action</Button>
    </AutobiaFlex>

    {/* Flex Wrap */}
    <AutobiaFlex
      wrap="wrap"
      gap={1}
      sx={{ p: 3, backgroundColor: 'gray.g50' }}
    >
      {tags.map(tag => <Chip key={tag} label={tag} />)}
    </AutobiaFlex>
  </Stack>
);

// Common Flex Patterns:
// justifyContent: 'center' | 'space-between' | 'space-around' | 'flex-start' | 'flex-end'
// alignItems: 'center' | 'flex-start' | 'flex-end' | 'stretch' | 'baseline'
// wrap: 'wrap' - Allow items to wrap to next line
// gap: number - Consistent spacing between all items`}
        />

        {/* Cards */}
        <LiveEditor
          title="Card Components"
          description="Content containers with headers, actions, and various layouts for displaying structured information."
          component={CardExample}
          code={`import AutobiaCard, { AutobiaStatsCard, AutobiaImageCard } from './components/Layout/AutobiaCard';

const Example = () => (
  <Grid container spacing={3}>
    {/* Basic Card */}
    <Grid item xs={12} md={6}>
      <AutobiaCard
        title="Basic Card"
        subtitle="This is a subtitle"
      >
        <Typography>Card content goes here...</Typography>
      </AutobiaCard>
    </Grid>

    {/* Card with Actions */}
    <Grid item xs={12} md={6}>
      <AutobiaCard
        title="Card with Actions"
        actions={
          <Stack direction="row" spacing={1}>
            <Button size="small">Cancel</Button>
            <Button size="small" variant="contained">Save</Button>
          </Stack>
        }
      >
        <Typography>Interactive card content...</Typography>
      </AutobiaCard>
    </Grid>

    {/* Stats Card */}
    <Grid item xs={12} md={3}>
      <AutobiaStatsCard
        title="Total Revenue"
        value="245,680 SAR"
        icon={<TrendingUp />}
        trend={12.5}
        trendLabel="vs last month"
        color="primary"
      />
    </Grid>

    {/* Image Card */}
    <Grid item xs={12} md={6}>
      <AutobiaImageCard
        image="/path/to/image.jpg"
        title="Product Card"
        description="Product description..."
        actions={
          <Button variant="contained">Buy Now</Button>
        }
      />
    </Grid>
  </Grid>
);

// AutobiaCard Props:
// title: string - Card title
// subtitle: string - Card subtitle  
// avatar: ReactNode - Avatar in header
// action: ReactNode - Action button in header
// image: string - Header image URL
// actions: ReactNode - Bottom action buttons
// elevation: number - Card shadow depth
// variant: 'elevation' | 'outlined' - Card style
// hoverable: boolean - Hover animation
// clickable: boolean - Click interaction

// AutobiaStatsCard Props:
// title: string - Stat title
// value: string|number - Main value
// icon: ReactNode - Icon element
// trend: number - Percentage change
// trendLabel: string - Trend description
// color: 'primary'|'secondary'|'success'|'error'|'warning'|'info'

// AutobiaImageCard Props:
// image: string - Image URL
// title: string - Card title
// description: string - Card description
// actions: ReactNode - Action buttons
// imageHeight: number - Image height in pixels`}
        />
      </Stack>

      {/* Usage Guidelines */}
      <Box sx={{ mt: 8, p: 4, backgroundColor: 'gray.g25', borderRadius: 2 }}>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 3 }}>
          Layout Guidelines
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Grid System
            </Typography>
            <Typography variant="body2" color="gray.g600" sx={{ mb: 2 }}>
              Use the 12-column grid system for responsive layouts. Always start with mobile-first design (xs) and progressively enhance for larger screens.
            </Typography>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Spacing Consistency
            </Typography>
            <Typography variant="body2" color="gray.g600">
              Use consistent spacing values (multiples of 8px) throughout your layouts. The spacing prop accepts numbers that multiply by 8px.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Content Hierarchy
            </Typography>
            <Typography variant="body2" color="gray.g600" sx={{ mb: 2 }}>
              Use cards to group related content and create visual hierarchy. Stats cards are perfect for dashboards and metrics display.
            </Typography>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Responsive Design
            </Typography>
            <Typography variant="body2" color="gray.g600">
              Always test layouts on different screen sizes. Use responsive stacks and grids to ensure content adapts gracefully across devices.
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Layout;